/**
 * @file Contains hook for generating success action after submitting a compose form
 */
import { useLocation, useNavigate } from 'react-router-dom';
import { OperationResult } from 'urql';

import { ROUTE__FEED, ROUTE__KANBAN } from '../../../constants/routing/routes';
import {
  MessageCreateMutation,
  TemplateCreateMutation,
  TemplateUpdateMutation,
} from '../../../generated/graphql';
import useParamHotelId from '../../../hooks/router/params/useParamHotelId';
import useIsTablet from '../../../hooks/useIsTablet';
import useToast from '../../../hooks/useToast';
import translate from '../../../i18n/translate';
import { ToastAction } from '../../../models/toastMessage';
import { getMessageDetailsUrl } from '../../../routes/urls/message';

type SubmitMutations =
  | MessageCreateMutation
  | TemplateCreateMutation
  | TemplateUpdateMutation;

/**
 * Get success action after submitting a compose form
 *
 * @returns Callback for calling toast after successfully submitting a compose form
 */
const useComposeSuccessToast = () => {
  const hotelId = useParamHotelId();
  const isTablet = useIsTablet();
  const location = useLocation();
  const navigate = useNavigate();
  const pushToast = useToast();

  /**
   * Callback for calling toast after successfully submitting a compose form
   *
   * @param mutationResponse Response from submit mutation
   */
  const pushSuccessToast = (
    mutationResponse: OperationResult<SubmitMutations>,
  ) => {
    if (
      mutationResponse.data !== undefined &&
      'messageCreate' in mutationResponse.data
    ) {
      const { id, topic, status } = mutationResponse.data.messageCreate;

      const messageUrl = getMessageDetailsUrl(
        hotelId,
        isTablet,
        location,
        id,
        topic.id,
        status ? ROUTE__KANBAN : ROUTE__FEED,
      );

      const translationKeys = status
        ? { actionText: 'GENERAL__GO_TO_TASK', text: 'TASK__CREATED_SUCCESS' }
        : { actionText: 'GENERAL__GO_TO_POST', text: 'POST__CREATED_SUCCESS' };

      const customAction: ToastAction = {
        /**
         * Navigate to the message details page
         */
        callback: () => {
          navigate(messageUrl);
        },
        text: translate(translationKeys.actionText),
      };

      const text = translate(translationKeys.text, topic.title);

      pushToast({ customAction, text, type: 'success' });
    }
  };

  return pushSuccessToast;
};

export default useComposeSuccessToast;
